/**
 * Functions to load assets for the Delivery System.
 */

import Radio from 'backbone.radio';

import {definitions} from './FontFaceDefinitions';


const configChannel = Radio.channel('config');
const loadChannel = Radio.channel('load');
const viewChannel = Radio.channel('view');


// Whereas the "app" request is not used any more, let's throw an Error.
loadChannel.reply('app', () => {throw new Error('ERR-EGC-31: "app" on "load" channel');});


/**
 * Load a JavaScript file by creating a <script> element.
 *
 * @param {string} url - Full URL to load for the script.
 * @param {Object} [attributes] - Additional attributes that may be added to
 *     the <script> element.
 * @returns {Promise<void|Error>}
 * @throws When there is a problem loading the script file.
 * @async
 *
 * As of May 2023, this function appears to be used only when loading the
 * FastSpring store for MGG personal subscriptions.
 */
export async function loadScript(url, attributes)
{
    // Make sure it doesn't already exist; if it does, we don't need a new version
    url = convertToSafeURL(url);
    const existingNode = document.querySelector(`script[type="text/javascript"][src="${url}"]`);
    if (existingNode)
    {
        return;
    }

    // Append necessary attributes for this type
    if (!attributes)
    {
        attributes = {};
    }
    attributes.type = 'text/javascript';
    attributes.src = url;

    // Create the el and pass off to common handler
    const script = document.createElement('script');
    return appendEl(script, attributes);
}
loadChannel.reply('script', loadScript);


/**
 * Load a CSS file by creating a <link> element.
 *
 * @param {string} url - Full URL to load for the CSS.
 * @param {Object} [attributes] - Additional attributes that may be added to
 *     the <link> element.
 * @returns {Promise<void|Error>}
 * @throws When there is a problem loading the CSS file.
 * @async
 */
export async function loadCSS(url, attributes)
{
    // Make sure it doesn't already exist; if it does, we don't need a new version
    url = convertToSafeURL(url);
    const existingNode = document.querySelector(`link[rel="stylesheet"][href="${url}"]`);
    if (existingNode)
    {
        return;
    }

    // Append necessary attributes for this type
    if (!attributes)
    {
        attributes = {};
    }
    attributes.rel = 'stylesheet';
    attributes.href = url;

    // Create the el and pass off to common handler
    const link = document.createElement('link');
    return appendEl(link, attributes);
}
loadChannel.reply('css', loadCSS);


/**
 * Load a template file from the server and put it in the Marionette cache.
 *
 * @param {string} url - Full URL to load for the templates.
 * @param {string} [templateID] - Optional. Used for fetching HTML directly
 *     from the server (see below for explanation).
 * @returns {Promise<void|Error>}
 * @throws When there is a problem loading the script file.
 * @async
 *
 * To fetch HTML directly from the server. This requires the "templateID"
 * parameter and the server to return a string. In this case, this function
 * assumes (without any verification) that the returned string contains HTML
 * for a single template. The string is added to the template cache using the
 * "templateID" as a key in the cache. As of 31 March 2023, this functionality
 * is only used with the DS Server for "content" endpoints that are served
 * directly by NGINX (and do not go through the DS Server).
 */
export async function loadTemplates(url, templateID)
{
    /* eslint-disable no-console */
    // Note: This function uses console.error() directly so that we don't
    // have to import printDeveloperError() so early in the app initialization.

    const response = await fetch(convertToSafeURL(url));

    if (!response.ok)
    {
        const msg = `ERR-EGC-11: loadTemplates() received HTTP ${response.status}`;
        console.error(msg);
        throw new Error(msg);
    }
    else
    {
        const contentType = response.headers.get('content-type');

        if (contentType.includes('application/json'))
        {
            viewChannel.trigger(
                'templates:register',
                await response.json()
            );
        }
        else if (contentType.includes('text/html'))
        {
            if (templateID)
            {
                viewChannel.trigger(
                    'templates:register',
                    {[templateID]: await response.text()}
                );
            }
            else
            {
                const msg = 'ERR-EGC-12: no "templateID"';
                console.error(msg);
                throw new Error(msg);
            }
        }
        else
        {
            const msg = `ERR-EGC-13 with content-type: "${contentType}"`;
            console.error(msg);
            throw new Error(msg);
        }
    }
    /* eslint-enable */
}
loadChannel.reply('templates', loadTemplates);


/**
 * Complete an asset URL for Egret Delivery System expectations.
 *
 * @param {string} url - String with URL or pathname to load.
 * @returns {string} The "safe" (that is: Egret-proof) URL.
 *
 * This function does two things:
 * - If `window.testRoot` is defined and the "url" argument does not begin with
 *     "http" then the function assumes the argument is just the URL pathname,
 *     so the value of `window.root` is prepended. Among other things, this
 *     feature allows the Karma test harness to load assets more easily.
 * - Add the "v" search parameter to the end of the string, equal to the current
 *     Delivery System "version." This is a cache-busting mechanism.
 */
function convertToSafeURL(url)
{
    // see loader-es6.js for `runningTests` documentation
    if (window.testRoot && !url.startsWith('http'))
    {
        url = window.root + url;
    }

    // Help avoid caching issues
    const rootController = viewChannel.request('controller', 'rootController');
    let version;
    if (rootController && rootController.currentRelease)
    {
        version = rootController.currentRelease;
    }
    else if (configChannel.request('property', 'version'))
    {
        version = configChannel.request('property', 'version');
    }

    if (version)
    {
        const joinChar = url.indexOf('?') > -1 ? '&' : '?';
        url += joinChar + 'v=' + version;
    }

    return url;
}


/**
 * Wait for an Element to trigger the "onload" or "onerror" function.
 *
 * @param {Element} el - To add to the DOM tree.
 * @param {Object} attributes - To add to the element.
 * @returns {Promise<void|Error>} - Resolves when the element successfully
 *     loads its linked content. Rejects with an Error when the element
 *     is not able to load its linked content.
 * @async
 */
function appendEl(el, attributes)
{
    return new Promise(function (resolve, reject)
    {
        // IE
        if (el.readyState)
        {
            el.onreadystatechange = function ()
            {
                if (el.readyState === 'loaded' || el.readyState === 'complete')
                {
                    el.onreadystatechange = null;
                    resolve();
                }
            };
        }
        // Others
        else
        {
            el.onload = () => resolve();
            el.onerror = () =>
            {
                // If the asset fails to load, we can provide a minimal failure message.
                const assetURL = typeof attributes.href === 'string'
                    ? attributes.href
                    : attributes.src;
                const errMsg = typeof assetURL === 'string'
                    ? `Failed to load "${assetURL}".`
                    : 'Failed to load asset.';
                reject(new Error(errMsg));
            };
        }


        // Add on all necessary attributes
        if (attributes)
        {
            Object.keys(attributes).map(attr => el.setAttribute(attr, attributes[attr]));
        }

        const head = document.querySelector('head');
        if (head)
        {
            head.appendChild(el);
        }
        else
        {
            reject(
                new Error('appendEl(): did not find "head" element')
            );
        }
    });
}


/**
 * Create font face definitions and load them.
 *
 * @param {FontSpec[]} fontSpecs - Specifications for the fonts to load.
 * @param {boolean} [loadImmediately=true] - Whether to call load() on the
 *     FontFace instance, thereby requesting the browser to load the font
 *     file immediately. Defaults to true, and this should only be set to
 *     false for files we aren't sure will be needed (for example, BravuraText,
 *     or the Vietnamese glyphs of EB Garamond).
 * @returns {Promise<*>} An "all settled" promise for all the fonts requested.
 * @async
 *
 * @typedef {Object} FontSpec
 * @property {string} family - Name of the font family being defined.
 * @property {string} source - URL in a string (do not give an ArrayBuffer).
 * @property {Object} descriptors - Passed as-is to the FontFace constructor.
 */
async function loadFonts(fontSpecs, loadImmediately = true)
{
    if (Array.isArray(fontSpecs))
    {
        return Promise.allSettled(
            fontSpecs.map((spec) =>
            {
                const thisFont = new FontFace(
                    spec.family,
                    `url("${spec.source}")`,
                    spec.descriptors
                );
                document.fonts.add(thisFont);
                return loadImmediately === true ?
                    thisFont.load() :
                    Promise.resolve();
            })
        );
    }
}


/**
 * Load the primary font files for this site.
 *
 * @returns {Promise<void>} An "all settled" promise for all the primary
 *     font files for this site.
 * @async
 *
 * Primary fonts are the core fonts required immediately, and on every page.
 */
export async function loadPrimaryFonts()
{
    const site = configChannel.request('property', 'site');

    const fontsToLoad = [];

    if (site === 'MGG')
    {
        fontsToLoad.push(definitions.Skolar.primary[0]);
    }

    if (site === 'MGG' || site === 'RME')
    {
        fontsToLoad.push(definitions.SkolarSans.primary[0]);
    }

    if (site === 'DEU')
    {
        fontsToLoad.push(definitions.Garamond.primary[0]);
        fontsToLoad.push(definitions.Metropolis.primary[0]);
    }

    return loadFonts(fontsToLoad);
}


/**
 * Load the secondary font files for this site.
 *
 * @returns {Promise<*>} An "all settled" promise for all the secondary
 *     font files for this site.
 * @async
 *
 * Secondary fonts include icons. Without these fonts, obvious "mojibake"
 * characters are rendered.
 */
export async function loadSecondaryFonts()
{
    const site = configChannel.request('property', 'site');

    const fontsToLoad = [];

    // Load FontAwesome.
    if (site === 'MGG' || site === 'RME' || site === 'DEU')
    {
        fontsToLoad.push(
            definitions.FontAwesome.secondary[0]
        );
    }

    return loadFonts(fontsToLoad);
}


/**
 * Load the tertiary font files for this site.
 *
 * @returns {Promise<*>} An "all settled" promise for all the tertiary
 *     font files for this site.
 * @async
 *
 * Tertiary fonts are variations on the primary fonts, for example bold
 * and italic styles.
 */
export async function loadTertiaryFonts()
{
    const site = configChannel.request('property', 'site');

    const fontsToLoad = [];

    if (site === 'MGG')
    {
        definitions.Skolar.tertiary.forEach(
            declaration => fontsToLoad.push(declaration)
        );
    }

    if (site === 'MGG' || site === 'RME')
    {
        definitions.SkolarSans.tertiary.forEach(
            declaration => fontsToLoad.push(declaration)
        );
    }

    if (site === 'DEU')
    {
        definitions.Garamond.tertiary.forEach(
            declaration => fontsToLoad.push(declaration)
        );
        definitions.Metropolis.tertiary.forEach(
            declaration => fontsToLoad.push(declaration)
        );
    }

    return loadFonts(fontsToLoad);
}


/**
 * Load the quaternary font files for this site.
 *
 * @returns {Promise<*>} An already-resolved Promise.
 * @async
 *
 * Quaternary fonts are only required for select article pages. Note that these
 * font files are not necessarily loaded immediately. This function defines the
 * font families for the browser, then leaves it to the browser to decide when
 * to load the files. Therefore, the Promise is always resolved immediately,
 * unless it rejects with an error.
 */
export async function loadQuaternaryFonts()
{
    const site = configChannel.request('property', 'site');

    // Set up "Bravura Text" for all sites.
    const fontsToLoad = [];

    // Set up additional Unicode ranges for EB Garamond on DEUMM.
    if (site === 'DEU')
    {
        definitions.Garamond.quaternary.forEach(
            declaration => fontsToLoad.push(declaration)
        );
    }

    return loadFonts(fontsToLoad, false);
}
loadChannel.reply('fonts:quaternary', loadQuaternaryFonts);


/**
 * Load any font files required to display SMUFL characters.
 *
 * @returns {Promise<*>} An "all settled" promise for all the tertiary
 *     font files for this site.
 * @async
 */
export async function loadSMUFLFonts()
{
    return loadFonts(definitions.BravuraText.quaternary);
}
loadChannel.reply('fonts:smufl', loadSMUFLFonts);


/**
 * Ensure all primary, secondary, and tertiary fonts are loaded.
 *
 * @returns {Promise<void>}
 */
export async function loadSupplementalFonts()
{
    // Note 1: Browsers avoid duplicating font-face declarations, so we don't
    //     need to check for that. Just try to load the font.
    // Note 2: Whereas all browsers that support the CSS Font Loading API also
    //     support WOFF2 font files, we don't need to use any fallbacks.

    // Double-check that the primary fonts have finished loading. If not, we
    // don't want to load any more fonts yet.
    await loadPrimaryFonts();

    // Secondary fonts: icons.
    await loadSecondaryFonts();

    // Tertiary fonts: variations on the core fonts.
    await loadTertiaryFonts();
}
